import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MoreHorizontal, Ticket } from "lucide-react";
import GravatarAvatar from "@/components/GravatarAvatar/GravatarAvatar";
import {
  AttendanceGridItem,
  DayStatus,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";

interface AttendanceCardProps {
  item: AttendanceGridItem;
  dayStatus: DayStatus;
  isFinalized: boolean;
  onCycleStatus: (attendanceId: number, currentStatus: string) => void;
  onOpenDetail: (item: AttendanceGridItem, dayStatus: DayStatus) => void;
}

export default function AttendanceCard({
  item,
  dayStatus,
  isFinalized,
  onCycleStatus,
  onOpenDetail,
}: AttendanceCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dayStatus.attendanceId.toString(),
    disabled: isFinalized,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (isDragging || isFinalized) return;
    onCycleStatus(dayStatus.attendanceId, dayStatus.status);
  };

  const handleDetailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenDetail(item, dayStatus);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`p-3 bg-white dark:bg-slate-800 rounded-md border border-gray-100 dark:border-slate-700 transition-colors ${
        isFinalized
          ? "cursor-default"
          : "cursor-pointer hover:border-green-300 dark:hover:border-green-700 active:bg-green-50 dark:active:bg-green-950/20"
      }`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <GravatarAvatar
            email={item.attendee?.email}
            firstName={item.attendee?.firstName}
            lastName={item.attendee?.lastName}
            size="sm"
          />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {item.attendee?.fullName || "Unknown"}
            </p>
            {item.attendee?.email && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                {item.attendee.email}
              </p>
            )}
          </div>
          {item.usedVoucher && (
            <span className="flex-shrink-0" title="Voucher attendee">
              <Ticket className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
            </span>
          )}
        </div>
        {!isFinalized && (
          <button
            className="text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors flex-shrink-0 ml-2 p-0.5 -m-0.5 rounded"
            onClick={handleDetailClick}
            title="View details"
            type="button"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {item.specialRequests && (
        <p className="text-xs mt-1.5 line-clamp-2">
          <span className="font-medium text-amber-600 dark:text-amber-400">
            Special Request:
          </span>{" "}
          <span className="text-amber-700 dark:text-amber-400/80">
            {item.specialRequests}
          </span>
        </p>
      )}
      {dayStatus.notes && (
        <p className="text-xs mt-1 line-clamp-2">
          <span className="font-medium text-gray-500 dark:text-gray-400">
            Note:
          </span>{" "}
          <span className="text-gray-400 dark:text-gray-500">
            {dayStatus.notes}
          </span>
        </p>
      )}
    </div>
  );
}
